import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  UserCheck,
  UserPlus,
  Trophy,
  CreditCard,
  Activity,
  PieChart,
  TrendingUp,
  Smartphone,
  Banknote,
  Award,
  AlertCircle,
  Clock,
  Search,
  Loader2,
} from "lucide-react";
import { formatCurrency } from '../../../utils/format';
// Import file service của bạn (giả định)
import { merchantService } from '../../../services/apiService';
import { useDispatch, useSelector } from "react-redux";
import { setMerchantId as setGlobalMerchantId } from "../../../features/global/globalSlice";

// --- BẢN ĐỒ STYLE CHO CÁC NHÓM KHÁCH HÀNG ---
const SEGMENT_STYLES = {
  VIP: { color: "bg-yellow-500", text: "text-yellow-600", icon: Trophy },
  Loyalty: { color: "bg-green-500", text: "text-green-600", icon: Award },
  "Vãng Lai": { color: "bg-blue-500", text: "text-blue-600", icon: Users },
  Churn: { color: "bg-red-500", text: "text-red-600", icon: AlertCircle },
  Default: { color: "bg-slate-500", text: "text-slate-600", icon: Users }, // Fallback
};

// --- DỮ LIỆU GIẢ LẬP CHO CÁC MỤC KHÁC CHƯA CÓ API ---
const demographicData = {
  gender: [
    { name: "Nữ", percent: 65, color: "bg-pink-400" },
    { name: "Nam", percent: 35, color: "bg-blue-400" },
  ],
  age: [
    { range: "18-24", percent: 45, spend: 8500000 },
    { range: "25-34", percent: 35, spend: 12000000 },
    { range: "35-44", percent: 15, spend: 4500000 },
    { range: "45+", percent: 5, spend: 1200000 },
  ],
};

const paymentTrends = [
  {
    method: "Quét mã QR",
    percent: 75,
    icon: Smartphone,
    color: "text-indigo-600",
    bg: "bg-indigo-100",
  },
  {
    method: "Tiền mặt",
    percent: 15,
    icon: Banknote,
    color: "text-green-600",
    bg: "bg-green-100",
  },
  {
    method: "Thẻ ngân hàng",
    percent: 10,
    icon: CreditCard,
    color: "text-blue-600",
    bg: "bg-blue-100",
  },
];
const getFirstAndLastDayOfMonth = () => {
  const now = new Date();

  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const format = (d) => d.toISOString().slice(0, 10);

  return {
    startDate: format(firstDay),
    endDate: format(lastDay),
  };
};

export default function MerchantDashboard() {
  const dispatch = useDispatch();
  const currentMerchantId = useSelector((state) => state.global.merchant_id);
  // States cho Form Input
  const [merchantId, setMerchantId] = useState(currentMerchantId || "");
  const { startDate: initStartDate, endDate: initEndDate } =
    getFirstAndLastDayOfMonth();
  const [startDate, setStartDate] = useState(initStartDate);
  const [endDate, setEndDate] = useState(initEndDate);

  // States cho Data API
  const [overviewData, setOverviewData] = useState(null);
  const [segmentData, setSegmentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Hàm gọi API
  // 🚀 CẬP NHẬT: Dùng useCallback để tránh re-render function
  const fetchData = useCallback(async (idToFetch = currentMerchantId) => {
    if (!idToFetch) {
      setError("Vui lòng nhập Merchant ID");
      return;
    }

    // Bật trạng thái loading và xóa lỗi
    setLoading(true);
    setError("");

    // 🚀 THÊM BƯỚC NÀY: Xóa tạm dữ liệu cũ để ép giao diện hiển thị trạng thái Loading
    // Điều này tạo cảm giác "refresh" dữ liệu cho người dùng
    setOverviewData(null);
    setSegmentData([]);

    try {
      // 🚀 THÊM LOG ĐỂ KIỂM TRA: Mở F12 -> Console để xem ID mới có được truyền vào chưa
      console.log("Đang gọi API với Merchant ID:", idToFetch);

      const retentionRes = await merchantService.getCustomerRetention(idToFetch, startDate, endDate).catch(e => {
        console.warn("Lỗi getCustomerRetention:", e.message);
        return { data: null };
      });
      const segmentRes = await merchantService.getSegmentSummary(idToFetch).catch(e => {
        console.warn("Lỗi getSegmentSummary:", e.message);
        return { data: [] };
      });

      const rawSegmentsArray = Array.isArray(segmentRes.data)
        ? segmentRes.data
        : Array.isArray(segmentRes.data?.data)
          ? segmentRes.data.data
          : [];

      const formattedSegments = rawSegmentsArray.map((item) => ({
        ...item,
        ...(SEGMENT_STYLES[item.segment_name] || SEGMENT_STYLES["Default"]),
      }));

      // Cập nhật dữ liệu mới
      setOverviewData(retentionRes?.data || retentionRes);
      setSegmentData(formattedSegments);
    } catch (err) {
      console.error(err);
      setError("Có lỗi xảy ra khi tải dữ liệu. Vui lòng thử lại!");
    } finally {
      // Tắt trạng thái loading
      setLoading(false);
    }
  }, [currentMerchantId, startDate, endDate]);

  useEffect(() => {
    setMerchantId(currentMerchantId || "");

    if (currentMerchantId) {
      fetchData(currentMerchantId);
      return;
    }

    setOverviewData(null);
    setSegmentData([]);
  }, [currentMerchantId, startDate, endDate, fetchData]);

  const handleSearch = () => {
    const normalizedMerchantId = merchantId.trim();

    if (!normalizedMerchantId) {
      setError("Vui lòng nhập Merchant ID");
      return;
    }

    if (normalizedMerchantId !== currentMerchantId) {
      dispatch(setGlobalMerchantId(normalizedMerchantId));
      return;
    }

    fetchData(normalizedMerchantId);
  };

  // Tính toán các thông số phụ thuộc (chỉ chạy khi có segmentData)
  const bestSegment =
    segmentData.length > 0
      ? [...segmentData].sort((a, b) => b.total_revenue - a.total_revenue)[0]
      : null;
  const totalRevenue = segmentData.reduce(
    (sum, item) => sum + item.total_revenue,
    0,
  );

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8 font-sans text-slate-800">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          {/* <h1 className="text-3xl font-bold text-slate-900">
            Customer 360° Insights
          </h1> */}
          {overviewData && (
            <p className="text-slate-500 mt-1">
              Merchant ID:{" "}
              <span className="font-semibold text-indigo-600">
                {overviewData.merchant_id}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* 🔍 Bộ lọc / Tham số đầu vào */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 mb-8 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-slate-600 mb-1">
            Merchant ID
          </label>
          <input
            type="text"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
            placeholder="Nhập Merchant ID..."
          />
        </div>
        <div className="flex-1 min-w-[150px]">
          <label className="block text-sm font-medium text-slate-600 mb-1">
            Từ ngày
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
          />
        </div>
        <div className="flex-1 min-w-[150px]">
          <label className="block text-sm font-medium text-slate-600 mb-1">
            Đến ngày
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2 h-[42px]"
        >
          {loading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Search size={18} />
          )}
          Tra cứu
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Chỉ hiển thị Data khi overviewData đã được tải */}
      {!overviewData && !loading && !error && (
        <div className="text-center py-12 text-slate-500">
          <Search size={48} className="mx-auto text-slate-300 mb-4" />
          <p>
            Nhập thông tin và bấm <b>Tra cứu</b> để hiển thị dữ liệu.
          </p>
        </div>
      )}

      {overviewData && segmentData.length > 0 && (
        <>
          {/* Row 1: KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Tổng khách hàng */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium text-slate-500">
                    Tổng khách hàng
                  </p>
                  <h3 className="text-3xl font-bold mt-2">
                    {overviewData.total_customers}
                  </h3>
                </div>
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg">
                  <Users size={24} />
                </div>
              </div>
              <div className="mt-4">
                <div className="w-full bg-slate-100 rounded-full h-2 mb-2 flex overflow-hidden">
                  <div
                    className="bg-indigo-500 h-2"
                    style={{ width: `${overviewData.old_rate}%` }}
                  ></div>
                  <div
                    className="bg-emerald-400 h-2"
                    style={{ width: `${overviewData.new_rate}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs font-medium text-slate-500">
                  <span className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-indigo-500 mr-1"></span>{" "}
                    Cũ ({overviewData.old_rate}%)
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 mr-1"></span>{" "}
                    Mới ({overviewData.new_rate}%)
                  </span>
                </div>
              </div>
            </div>

            {/* Khách hàng cũ */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 flex flex-col justify-center">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-indigo-50 text-indigo-600 rounded-full">
                  <UserCheck size={28} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">
                    Khách hàng cũ
                  </p>
                  <h3 className="text-2xl font-bold mt-1">
                    {overviewData.old_customers}{" "}
                    <span className="text-sm font-normal text-slate-400 ml-1">
                      người
                    </span>
                  </h3>
                </div>
              </div>
            </div>

            {/* Khách hàng mới */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 flex flex-col justify-center">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-emerald-50 text-emerald-600 rounded-full">
                  <UserPlus size={28} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-500">
                    Khách hàng mới
                  </p>
                  <h3 className="text-2xl font-bold mt-1">
                    {overviewData.new_customers}{" "}
                    <span className="text-sm font-normal text-slate-400 ml-1">
                      người
                    </span>
                  </h3>
                </div>
              </div>
            </div>

            {/* Nhóm tốt nhất */}
            {bestSegment && (
              <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-xl p-6 shadow-md text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-indigo-100 text-sm font-medium">
                      Doanh thu tốt nhất
                    </p>
                    <h3 className="text-2xl font-bold mt-1 flex items-center gap-2">
                      <Trophy size={20} className="text-yellow-300" /> Nhóm{" "}
                      {bestSegment.segment_name}
                    </h3>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-indigo-500/30">
                  <p className="text-indigo-100 text-xs mb-1">
                    Đóng góp doanh thu
                  </p>
                  <p className="text-xl font-bold">
                    {formatCurrency(bestSegment.total_revenue)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Row 2: Phân nhóm khách hàng & Tần suất giao dịch */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Table Phân Nhóm */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 lg:col-span-2 overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <PieChart size={20} className="text-indigo-600" /> Phân Tích &
                  Doanh Thu Theo Nhóm
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-sm border-b border-slate-100">
                      <th className="p-4 font-medium">Nhóm Khách Hàng</th>
                      <th className="p-4 font-medium text-right">Số Lượng</th>
                      <th className="p-4 font-medium text-right">
                        Tổng Doanh Thu
                      </th>
                      <th className="p-4 font-medium text-right">
                        % Doanh thu
                      </th>
                      <th className="p-4 font-medium text-right">
                        Chi Tiêu TB
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {segmentData.map((row, idx) => {
                      const Icon = row.icon;
                      const revenuePercent =
                        totalRevenue > 0
                          ? ((row.total_revenue / totalRevenue) * 100).toFixed(
                              1,
                            )
                          : 0;
                      return (
                        <tr
                          key={idx}
                          className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors"
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`p-2 rounded-lg ${row.color} bg-opacity-10 ${row.text}`}
                              >
                                <Icon size={18} />
                              </div>
                              <span className="font-semibold text-slate-700">
                                {row.segment_name}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-right font-medium text-slate-600">
                            {row.total_customers}
                          </td>
                          <td className="p-4 text-right font-bold text-slate-800">
                            {formatCurrency(row.total_revenue)}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <span className="text-sm font-medium">
                                {revenuePercent}%
                              </span>
                              <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${row.color}`}
                                  style={{ width: `${revenuePercent}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-right text-slate-500">
                            {formatCurrency(row.avg_ticket_size)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tần suất giao dịch TB */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-6">
                <Activity size={20} className="text-indigo-600" /> Tần Suất Giao
                Dịch
              </h2>
              <div className="space-y-5">
                {segmentData.map((row, idx) => {
                  const frequency =
                    row.total_customers > 0
                      ? (row.total_transactions / row.total_customers).toFixed(
                          1,
                        )
                      : 0;
                  // Tính maxFreq an toàn
                  const validFreqs = segmentData.map((r) =>
                    r.total_customers > 0
                      ? r.total_transactions / r.total_customers
                      : 0,
                  );
                  const maxFreq = Math.max(...validFreqs) || 1;
                  const percent = (frequency / maxFreq) * 100;

                  return (
                    <div key={idx}>
                      <div className="flex justify-between items-end mb-1">
                        <span className="text-sm font-medium text-slate-600">
                          {row.segment_name}
                        </span>
                        <span className="text-lg font-bold text-slate-800">
                          {frequency}{" "}
                          <span className="text-xs font-normal text-slate-500">
                            lần/KH
                          </span>
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2.5">
                        <div
                          className={`${row.color} h-2.5 rounded-full`}
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-100 flex gap-3">
                <Trophy className="text-amber-500 shrink-0" size={20} />
                <p className="text-sm text-amber-800">
                  Nhóm <span className="font-bold">VIP</span> có mức độ quay lại
                  cực cao. Cần có chính sách chăm sóc đặc biệt.
                </p>
              </div>
            </div>
          </div>

          {/* ... (Giữ nguyên Row 3 Chân dung KH và Xu hướng thanh toán MockData ở đây) ... */}
          {/* Row 3: Chân dung khách hàng & Xu hướng thanh toán (Fake Data) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chân dung khách hàng (Tuổi & Giới tính) */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 lg:col-span-2">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-6">
                <Users size={20} className="text-indigo-600" /> Chân Dung Khách
                Hàng Tiềm Năng
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Giới tính */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 mb-4 uppercase tracking-wider">
                    Tỷ lệ giới tính
                  </h3>
                  <div className="flex items-center gap-6">
                    <div
                      className="relative w-32 h-32 rounded-full flex items-center justify-center"
                      style={{
                        background: `conic-gradient(#f472b6 0% 65%, #60a5fa 65% 100%)`,
                      }}
                    >
                      <div className="w-24 h-24 bg-white rounded-full flex flex-col items-center justify-center shadow-inner">
                        <span className="text-xl font-bold text-slate-800">
                          100%
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {demographicData.gender.map((g, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span
                            className={`w-3 h-3 rounded-full ${g.color}`}
                          ></span>
                          <span className="font-medium text-slate-700">
                            {g.name}
                          </span>
                          <span className="text-slate-500 font-bold ml-2">
                            {g.percent}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Độ tuổi & Chi tiêu */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 mb-4 uppercase tracking-wider">
                    Độ tuổi & Mức chi tiêu
                  </h3>
                  <div className="space-y-4">
                    {demographicData.age.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <div className="w-14 text-sm font-semibold text-slate-600">
                          {item.range}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-slate-500">
                              {item.percent}% KH
                            </span>
                            <span className="font-medium text-indigo-600">
                              {formatCurrency(item.spend)}
                            </span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-1.5">
                            <div
                              className="bg-indigo-500 h-1.5 rounded-full"
                              style={{ width: `${item.percent}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Xu hướng thanh toán */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-6">
                <TrendingUp size={20} className="text-indigo-600" /> Xu Hướng
                Thanh Toán
              </h2>

              <div className="space-y-4">
                {paymentTrends.map((trend, idx) => {
                  const Icon = trend.icon;
                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-xl border border-slate-50 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2.5 rounded-lg ${trend.bg} ${trend.color}`}
                        >
                          <Icon size={20} />
                        </div>
                        <span className="font-semibold text-slate-700">
                          {trend.method}
                        </span>
                      </div>
                      <span className="text-lg font-bold text-slate-800">
                        {trend.percent}%
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100">
                <h3 className="text-sm font-semibold text-slate-500 mb-3 flex items-center gap-1">
                  <Clock size={16} /> Khung giờ Vàng giao dịch
                </h3>
                <div className="flex gap-2">
                  <span className="px-3 py-1 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-full border border-indigo-100">
                    07:00 - 09:00
                  </span>
                  <span className="px-3 py-1 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-full border border-indigo-100">
                    12:00 - 13:30
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
